import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Sparkles,
  Plus,
  Search,
  MoreVertical,
  Trash2,
  Pencil,
  ExternalLink,
  LogOut,
  FolderOpen,
  Clock,
} from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { useProjectStore } from '../store/projectStore'

export default function Dashboard() {
  const navigate = useNavigate()
  const { user, signOut } = useAuthStore()
  const { projects, loading, fetchProjects, createProject, deleteProject } = useProjectStore()

  const [searchQuery, setSearchQuery] = useState('')
  const [showNewProjectModal, setShowNewProjectModal] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [newProjectDescription, setNewProjectDescription] = useState('')
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    if (!user) {
      navigate('/auth')
      return
    }
    fetchProjects(user.id)
  }, [user, navigate, fetchProjects])

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user || !newProjectName.trim()) return

    setCreating(true)
    const project = await createProject(user.id, newProjectName.trim(), newProjectDescription.trim())
    setCreating(false)

    if (project) {
      setShowNewProjectModal(false)
      setNewProjectName('')
      setNewProjectDescription('')
      navigate(`/editor/${project.id}`)
    }
  }

  const handleDeleteProject = async (projectId: string) => {
    if (confirm('Are you sure you want to delete this project?')) {
      await deleteProject(projectId)
    }
    setActiveDropdown(null)
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  const filteredProjects = projects.filter(
    (p) =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  if (!user) return null

  return (
    <div className="min-h-screen bg-midnight-950">
      {/* Header */}
      <header className="sticky top-0 z-50 px-6 py-4 bg-midnight-950/80 backdrop-blur-xl border-b border-midnight-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-ember-400 to-ember-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-midnight-950" />
            </div>
            <span className="font-display text-xl font-semibold text-white">Lumina</span>
          </Link>

          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowNewProjectModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-ember-400 to-ember-500 text-midnight-950 font-semibold rounded-lg hover:shadow-lg hover:shadow-ember-500/25 transition-all btn-primary"
            >
              <Plus className="w-4 h-4" />
              New Project
            </button>

            <div className="relative">
              <button
                onClick={() => setActiveDropdown(activeDropdown === 'user' ? null : 'user')}
                className="w-10 h-10 rounded-full bg-gradient-to-br from-ember-400/20 to-ember-600/10 flex items-center justify-center text-ember-400 font-semibold hover:from-ember-400/30 hover:to-ember-600/20 transition-colors"
              >
                {user.email?.[0].toUpperCase()}
              </button>

              {activeDropdown === 'user' && (
                <div className="absolute right-0 mt-2 w-48 bg-midnight-900 border border-midnight-700 rounded-xl shadow-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-midnight-700">
                    <p className="text-sm text-white font-medium truncate">{user.email}</p>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-2 px-4 py-3 text-midnight-300 hover:text-white hover:bg-midnight-800 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold text-white mb-1">Your Projects</h1>
            <p className="text-midnight-400">Create and manage your AI-generated websites</p>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-midnight-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search projects..."
              className="w-full md:w-64 pl-10 pr-4 py-2.5 bg-midnight-900 border border-midnight-700 rounded-xl text-white placeholder-midnight-500 focus:outline-none focus:border-ember-500 transition-colors"
            />
          </div>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-64 bg-midnight-900/50 rounded-2xl shimmer" />
            ))}
          </div>
        ) : filteredProjects.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-20"
          >
            <div className="w-20 h-20 rounded-2xl bg-midnight-900 flex items-center justify-center mb-6">
              <FolderOpen className="w-10 h-10 text-midnight-600" />
            </div>
            <h2 className="font-display text-2xl font-semibold text-white mb-2">
              {searchQuery ? 'No projects found' : 'No projects yet'}
            </h2>
            <p className="text-midnight-400 mb-6">
              {searchQuery
                ? 'Try a different search term'
                : 'Create your first AI-powered website'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => setShowNewProjectModal(true)}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-ember-400 to-ember-500 text-midnight-950 font-semibold rounded-xl hover:shadow-lg hover:shadow-ember-500/25 transition-all"
              >
                <Plus className="w-5 h-5" />
                Create Project
              </button>
            )}
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="group relative bg-midnight-900/50 border border-midnight-800 rounded-2xl overflow-hidden hover:border-ember-500/30 transition-all"
              >
                {/* Thumbnail */}
                <div
                  onClick={() => navigate(`/editor/${project.id}`)}
                  className="h-40 bg-gradient-to-br from-midnight-800 to-midnight-900 cursor-pointer relative overflow-hidden"
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Sparkles className="w-12 h-12 text-midnight-700" />
                  </div>
                  <div className="absolute inset-0 bg-midnight-950/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="flex items-center gap-2 px-4 py-2 bg-ember-500 text-midnight-950 font-semibold rounded-lg">
                      <ExternalLink className="w-4 h-4" />
                      Open Editor
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display text-lg font-semibold text-white truncate">
                        {project.name}
                      </h3>
                      {project.description && (
                        <p className="text-sm text-midnight-400 truncate mt-1">
                          {project.description}
                        </p>
                      )}
                    </div>

                    <div className="relative">
                      <button
                        onClick={() =>
                          setActiveDropdown(activeDropdown === project.id ? null : project.id)
                        }
                        className="p-1.5 text-midnight-500 hover:text-white hover:bg-midnight-800 rounded-lg transition-colors"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>

                      {activeDropdown === project.id && (
                        <div className="absolute right-0 mt-1 w-40 bg-midnight-800 border border-midnight-700 rounded-xl shadow-xl overflow-hidden z-10">
                          <button
                            onClick={() => {
                              navigate(`/editor/${project.id}`)
                              setActiveDropdown(null)
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-midnight-300 hover:text-white hover:bg-midnight-700 transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteProject(project.id)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-midnight-700 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-3 text-sm text-midnight-500">
                    <Clock className="w-4 h-4" />
                    {formatDate(project.updated_at)}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* New Project Modal */}
      {showNewProjectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-midnight-950/80 backdrop-blur-sm"
            onClick={() => setShowNewProjectModal(false)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative w-full max-w-md bg-midnight-900 border border-midnight-700 rounded-2xl p-6"
          >
            <h2 className="font-display text-2xl font-bold text-white mb-6">Create New Project</h2>
            <form onSubmit={handleCreateProject} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-midnight-300 mb-2">
                  Project Name
                </label>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="My Awesome Website"
                  className="w-full px-4 py-3 bg-midnight-800 border border-midnight-700 rounded-xl text-white placeholder-midnight-500 focus:outline-none focus:border-ember-500 transition-colors"
                  required
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-midnight-300 mb-2">
                  Description (optional)
                </label>
                <textarea
                  value={newProjectDescription}
                  onChange={(e) => setNewProjectDescription(e.target.value)}
                  placeholder="A brief description of your project..."
                  rows={3}
                  className="w-full px-4 py-3 bg-midnight-800 border border-midnight-700 rounded-xl text-white placeholder-midnight-500 focus:outline-none focus:border-ember-500 transition-colors resize-none"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowNewProjectModal(false)}
                  className="flex-1 px-4 py-3 text-midnight-300 hover:text-white border border-midnight-700 hover:border-midnight-600 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !newProjectName.trim()}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-ember-400 to-ember-500 text-midnight-950 font-semibold rounded-xl hover:shadow-lg hover:shadow-ember-500/25 transition-all disabled:opacity-50"
                >
                  {creating ? (
                    <div className="w-5 h-5 border-2 border-midnight-950 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Plus className="w-5 h-5" />
                      Create
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  )
}

